import type { TimedAtom } from '@ctcells/api'
import { t } from '@ctcells/i18n'
import { useState } from 'react'

import { Button } from '../../atoms/Button.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { Field } from '../../atoms/Field.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { TextInput } from '../../atoms/TextInput.tsx'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import type { ActionGate } from '../access.ts'

import { CutAtomChecks, TimedAtomPick } from './resumeRoute.tsx'
import { allChecked, type ResumeCheck } from './resumeRouteModel.ts'

/** 작업 상세 복구 줄에서 장비를 다시 움직이는 두 판정 — 고른 방식 · 현장 확인을 실어 보낸다 */
export interface ResumeRouteSubmit {
  reason?: string
  timedAtom?: TimedAtom
  physicalChecked?: true
  physicalChecks?: readonly ResumeCheck[]
}

/**
 * **작업 상세의 «복구 › 재개» · «복구 › 멈춘 동작부터 다시» 대화**(백엔드 0.28.15 · eric 2026-10-22).
 *
 * - `rerun_cut_atom`(확인 재개) — 확인할 것(`resume_checks` · 없으면 일반 한 칸)을 **전부 체크해야** 보낸다.
 *   본문에 `physical_checked: true` · `physical_checks` 를 싣는다.
 * - `resume` — 끊긴 시간 원자 방식을 고를 수 있을 때(`resume_timed_atom_choices` 가 둘)만 이 대화가 선다.
 * 확인 재개는 현장 확인과 사유가 필수이고, 일반 재개에는 사유를 싣지 않는다.
 * 🔴 **성공해야 닫힌다** — 컨테이너의 약속이 이행되면 부른 쪽이 닫는다(`ReasonModal` 과 같은 규칙).
 */
export function ResumeRouteModal({
  mode,
  batchId,
  checks,
  timedAtomChoices,
  error,
  onSubmit,
  onClose,
  disabled,
  disabledReason,
}: {
  mode: 'resume' | 'rerun_cut_atom'
  batchId: string
  checks: readonly ResumeCheck[]
  timedAtomChoices: readonly TimedAtom[]
  error: string | undefined
  onSubmit: (opts: ResumeRouteSubmit) => void
  onClose: () => void
} & ActionGate) {
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set())
  const [timedAtom, setTimedAtom] = useState<TimedAtom>('restart')
  const [reason, setReason] = useState('')
  const rerun = mode === 'rerun_cut_atom'
  const validation = useSubmitValidation((value: string) => ({
    reason: rerun && value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')
  const gateProps: ActionGate = disabled
    ? { disabled: true, disabledReason }
    : rerun && !allChecked(checks, checked)
      ? { disabled: true, disabledReason: t('reason.physicalChecksRequired') }
      : { disabled: false }
  const label = rerun ? t('action.rerunCutAtom') : t('action.resume')

  const submit = () => {
    if (gateProps.disabled) return
    if (
      !validation.validate(
        reason,
        document.getElementById(reasonField.inputProps.id)?.parentElement ?? null,
      )
    )
      return
    onSubmit({
      ...(timedAtomChoices.length >= 2 ? { timedAtom } : {}),
      ...(rerun
        ? { physicalChecked: true as const, physicalChecks: checks, reason: reason.trim() }
        : {}),
    })
  }

  return (
    <Modal
      size="md"
      footer={
        <>
          <Button onClick={onClose} size="pill">
            {t('action.cancel')}
          </Button>
          <Button onClick={submit} size="pill" variant="primary" {...gateProps}>
            {label}
          </Button>
        </>
      }
      onClose={onClose}
      title={`${t('action.recover')} › ${label}`}
    >
      <ValueRows rows={[{ key: 'batch', label: t('column.batch'), value: batchId }]} />
      {rerun ? <CutAtomChecks checked={checked} checks={checks} onChange={setChecked} /> : null}
      {rerun ? (
        <Field {...reasonField.fieldProps} label={t('field.reason')}>
          <TextInput
            {...reasonField.inputProps}
            onChange={(next) => {
              setReason(next)
              validation.change('reason', next)
            }}
            value={reason}
            wide
          />
        </Field>
      ) : null}
      <TimedAtomPick choices={timedAtomChoices} onChange={setTimedAtom} value={timedAtom} />
      {error === undefined ? null : (
        <p className="vissue" role="alert">
          {error}
        </p>
      )}
    </Modal>
  )
}
